import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Globe2,
  Settings2,
  Truck,
  ArrowRight,
  CheckCircle2,
  Package,
  Ship,
  Plane,
  Anchor,
} from "lucide-react";
import { useReveal } from "../lib/reveal";

const heroImg =
  "https://images.unsplash.com/photo-1713098965471-d324f294a71d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxnbG9iYWwlMjB0cmFkZSUyMHdvcmxkJTIwbWFwfGVufDB8fHx8MTc3OTA4NDIyMXww&ixlib=rb-4.1.0&q=85";

const introImg =
  "https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBwb3J0fGVufDB8fHx8MTc3OTA4NDIyMXww&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: ShieldCheck,
    title: "Reliable & Transparent",
    desc: "We prioritize trust and efficiency in every transaction.",
  },
  {
    icon: Globe2,
    title: "Global Network",
    desc: "Our connections span multiple countries to meet your business needs.",
  },
  {
    icon: Settings2,
    title: "Customized Solutions",
    desc: "Tailored import and export solutions to suit your requirements.",
  },
  {
    icon: Truck,
    title: "Fast & Secure",
    desc: "Timely deliveries with strict adherence to international trade standards.",
  },
];

const stats = [
  { value: "25+", label: "Countries Served" },
  { value: "500+", label: "Shipments Delivered" },
  { value: "98%", label: "On-Time Rate" },
  { value: "10+", label: "Years of Trade" },
];

const partners = ["Maersk", "Hapag-Lloyd", "DHL", "MSC", "FedEx", "CMA CGM", "Emirates SkyCargo", "ONE"];

export default function Home() {
  const ref = useReveal();

  return (
    <div ref={ref} data-testid="home-page" className="bg-[#F8FAFC]">
      {/* HERO */}
      <section data-testid="hero-section" className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={heroImg}
            alt="World trade routes"
            className="h-full w-full object-cover scale-105"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32 lg:pt-36 lg:pb-44">
          <div className="max-w-3xl">
            <span
              data-testid="hero-eyebrow"
              className="reveal inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-white/90"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#F26F21] animate-pulse" />
              Global Trade · Est. Network
            </span>
            <h1
              data-testid="hero-heading"
              className="reveal mt-6 font-[Outfit] text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.05]"
            >
              Connecting Global Markets
              <br />
              with <span className="text-[#F26F21]">Reliable Imports</span>
              <br />
              &amp; Exports.
            </h1>
            <p
              data-testid="hero-tagline"
              className="reveal mt-7 max-w-xl text-base sm:text-lg text-slate-200/90 leading-relaxed"
            >
              Your Trusted Partner in Global Trade — moving goods, building bridges and
              powering commerce across continents.
            </p>
            <div className="reveal mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#F26F21] px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-[#F26F21]/20 hover:bg-[#D95B14] hover:translate-y-[-1px] transition-all"
              >
                Explore Our Services <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-white/80 px-7 py-4 text-sm font-semibold text-white hover:bg-white hover:text-[#0E2A3F] transition-colors"
              >
                Get a Quote Today
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="reveal mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md">
            {stats.map((s) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[#0E2A3F]/40 px-6 py-7"
              >
                <div className="font-[Outfit] text-3xl lg:text-4xl font-bold text-white">{s.value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-300">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative chips */}
        <div className="absolute bottom-8 right-8 hidden lg:flex flex-col gap-3 text-white/70 text-xs">
          <div className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5">
            <Ship className="h-3.5 w-3.5" /> Ocean Freight
          </div>
          <div className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5">
            <Plane className="h-3.5 w-3.5" /> Air Cargo
          </div>
          <div className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5">
            <Anchor className="h-3.5 w-3.5" /> Port-to-Port
          </div>
        </div>
      </section>

      {/* PARTNER MARQUEE */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center gap-8 overflow-hidden">
          <span className="shrink-0 text-[11px] uppercase tracking-[0.24em] text-slate-500 font-medium">
            Trusted carriers
          </span>
          <div className="relative overflow-hidden flex-1">
            <div className="marquee-track flex gap-12 whitespace-nowrap">
              {[...partners, ...partners].map((p, i) => (
                <span
                  key={i}
                  className="font-[Outfit] text-lg font-semibold text-slate-400/80 tracking-tight"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section data-testid="intro-section" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 reveal">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
              About VRVR
            </span>
            <h2 className="mt-4 font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A3F] tracking-tight leading-tight accent-underline">
              Borderless commerce, built on trust.
            </h2>
            <p className="mt-10 text-base lg:text-lg text-[#475569] leading-relaxed">
              VRVR Import and Exports is a leading trading company specializing in the seamless
              import and export of a wide range of goods. With years of experience and a global
              network of partners, we ensure that your products move efficiently and safely
              across borders.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "End-to-end logistics & customs handling",
                "Documentation & regulatory compliance",
                "Trade consulting and route optimization",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#0E2A3F]">
                  <CheckCircle2 className="h-5 w-5 text-[#F26F21] mt-0.5 shrink-0" />
                  <span className="text-sm lg:text-base">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/about"
                data-testid="intro-cta-about"
                className="inline-flex items-center gap-2 rounded-md bg-[#0E2A3F] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0A1E2D] transition-colors"
              >
                Our Story <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0E2A3F] px-6 py-3 text-sm font-semibold text-[#0E2A3F] hover:bg-[#0E2A3F] hover:text-white transition-colors"
              >
                Talk to an Expert
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-2xl bg-[#F26F21]/10" />
              <div className="absolute -bottom-6 -right-6 h-40 w-40 rounded-2xl border-2 border-[#F26F21]" />
              <img
                src={introImg}
                alt="Shipping containers at port"
                className="relative rounded-2xl object-cover w-full h-[480px] shadow-xl"
              />
              <div className="absolute bottom-6 left-6 max-w-xs rounded-xl bg-white/95 backdrop-blur p-5 shadow-xl border border-slate-200">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-md bg-[#0E2A3F] text-white flex items-center justify-center">
                    <Package className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-[Outfit] font-bold text-[#0E2A3F]">FCL · LCL · Air</div>
                    <div className="text-xs text-slate-500">All cargo modes supported</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        data-testid="why-choose-us-section"
        className="relative py-24 lg:py-32 bg-[#0E2A3F] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
              Why Choose Us
            </span>
            <h2 className="mt-4 font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Four reasons businesses ship with VRVR.
            </h2>
            <p className="mt-5 text-slate-300 text-base lg:text-lg leading-relaxed">
              From single-shipment exporters to global supply chains, we deliver the same
              precision, transparency and care to every consignment.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`why-card-${i}`}
                className="reveal lift group relative rounded-xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm hover:bg-white/[0.08] hover:border-[#F26F21]/60"
              >
                <div className="absolute top-7 right-7 text-[10px] font-mono text-white/30">
                  0{i + 1}
                </div>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#F26F21]/15 text-[#F26F21] ring-1 ring-[#F26F21]/30 group-hover:bg-[#F26F21] group-hover:text-white transition-colors">
                  <f.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-[Outfit] text-xl font-semibold text-white">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-slate-300/90 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex justify-center reveal">
            <Link
              to="/services"
              data-testid="why-cta-services"
              className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-7 py-4 text-sm font-semibold text-white hover:bg-[#D95B14] transition-colors"
            >
              Learn More About Our Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E2A3F] to-[#0A1E2D] p-10 lg:p-16">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#F26F21]/20 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="font-[Outfit] text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  Ready to move your cargo?
                </h3>
                <p className="mt-4 text-slate-300 max-w-md">
                  Tell us about your shipment and we&apos;ll come back with a transparent quote
                  within 24 hours.
                </p>
              </div>
              <div className="flex md:justify-end gap-4 flex-wrap">
                <Link
                  to="/contact"
                  data-testid="cta-strip-quote"
                  className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-7 py-4 text-sm font-semibold text-white hover:bg-[#D95B14] transition-colors"
                >
                  Request a Quote <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-md border-2 border-white/40 px-7 py-4 text-sm font-semibold text-white hover:bg-white hover:text-[#0E2A3F] transition-colors"
                >
                  Why VRVR
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
